import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Settings2, Eye, FileText } from "lucide-react";

/**
 * Banner-CTA to the interactive Aanbouw/Uitbouw configurator.
 * Shown on /diensten/aanbouw, /diensten/uitbouw and their city variants.
 */
export function ConfiguratorCTA({ city }) {
  const subtitle = city
    ? `Voor uw woning in ${city} — configureer in 3 minuten en ontvang direct een persoonlijke offerte.`
    : "Configureer in 3 minuten online en ontvang direct een persoonlijke offerte op maat.";

  return (
    <section
      className="py-16 md:py-20 border-t border-koning-border bg-gradient-to-br from-koning-surface to-koning-bg relative overflow-hidden"
      data-testid="configurator-cta"
    >
      <div aria-hidden className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-koning-gold/10 blur-3xl" />
      <div className="max-w-[1300px] mx-auto px-6 md:px-12 lg:px-20 relative">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-koning-gold mb-4">
              <Sparkles size={14} /> Interactieve tool · gratis
            </div>
            <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight leading-tight mb-5">
              Ontwerp uw eigen aanbouw — <span className="text-koning-gold">live in 3D</span>
            </h2>
            <p className="text-koning-muted leading-relaxed max-w-2xl mb-8">
              {subtitle} Kies uit ruim 20 opties voor binnen- én buitenkant: afmetingen, kozijnen, gevel,
              daklicht, verlichting, verwarming en meer. U ziet uw aanbouw direct terug in onze visualisatie.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <Feature icon={Settings2} title="Volledig op maat" desc="Diepte, breedte, materialen, kleur — u bepaalt alles" />
              <Feature icon={Eye} title="3D voorbeeld" desc="Direct zien hoe het eruit komt te zien" />
              <Feature icon={FileText} title="PDF + offerte" desc="Download de configuratie en ontvang persoonlijke prijs" />
            </div>

            <Link
              to="/configurator"
              data-testid="configurator-cta-link"
              className="inline-flex items-center gap-2 bg-koning-gold text-koning-bg font-semibold px-7 py-4 hover:bg-koning-goldHover transition-colors"
            >
              Start de configurator <ArrowRight size={18} />
            </Link>
            <p className="text-xs text-koning-muted mt-3">Geen account nodig · 100% vrijblijvend</p>
          </div>

          {/* Mock preview */}
          <div className="hidden lg:block">
            <div className="relative aspect-[4/5] border border-koning-border bg-koning-surface overflow-hidden">
              <img
                src="https://storage.googleapis.com/offra-extension-1-2024-12-09/outside-medium.webp"
                alt="Voorbeeld aanbouw configurator"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-koning-bg/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="text-xs uppercase tracking-[0.2em] text-koning-gold mb-1">Voorbeeld</div>
                <div className="font-display text-lg">400 × 600 cm · keralite gevel · vouwdeur</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-koning-gold/10 flex items-center justify-center shrink-0">
        <Icon size={16} className="text-koning-gold" />
      </div>
      <div>
        <div className="text-white font-semibold text-sm mb-1">{title}</div>
        <div className="text-xs text-koning-muted leading-snug">{desc}</div>
      </div>
    </div>
  );
}
